import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { forwardRef, useId } from 'react'

import { Tooltip } from '@repo/ui'
import { cn } from '@repo/utils'

import { InputPhone } from './Phone'

import type { InputHTMLAttributes, ReactNode } from 'react'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  className?: string
  error?: ReactNode
  inputClassName?: string
  label?: ReactNode
  labelComment?: ReactNode
  labelInfo?: ReactNode
  tooltipClassName?: string
  transparent?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      autoComplete = 'off',
      className,
      error,
      inputClassName,
      label,
      labelComment,
      labelInfo,
      maxLength = 249,
      minLength = 0,
      name,
      required,
      tooltipClassName,
      transparent = false,
      type = 'text',
      ...props
    },
    ref,
  ) => {
    const randId = useId()
    const htmlId = id || name || randId

    let input = (
      <input
        {...props}
        id={htmlId}
        autoComplete={autoComplete}
        className={cn(
          'block w-full outline-0 rounded-md border-0 shadow-sm placeholder:text-grey px-3 py-2 text-inherit bg-white',
          '!ring-1 ring-inset ring-grey',
          'focus:ring-greyDark',
          'disabled:ring-greyLight disabled:focus:ring-grey',
          'read-only:ring-greyLight read-only:focus:ring-grey',
          transparent &&
            'shadow-none ring-transparent focus:ring-transparent disabled:ring-transparent read-only:ring-transparent',
          type === 'number' && 'text-right',
          error && 'ring-red focus:ring-red disabled:ring-red read-only:ring-red',
          inputClassName,
        )}
        maxLength={maxLength}
        minLength={required ? Math.max(minLength, 1) : minLength}
        name={name || htmlId}
        ref={ref}
        required={required}
        type={type}
      />
    )

    if (type === 'tel') {
      input = (
        <InputPhone
          {...props}
          id={htmlId}
          autoComplete={autoComplete}
          className="flex"
          maxLength={maxLength}
          minLength={required ? Math.max(minLength, 1) : minLength}
          name={name || htmlId}
          phoneProps={{
            countrySelectorStyleProps: {
              buttonClassName: cn(
                'shrink outline-0 !rounded-l-md border border-grey !shadow-sm placeholder:text-grey pl-3 pr-1 py-2 text-inherit !bg-white',
                'ring-0 ring-greyDark',
                'focus:border-transparent focus:ring-1 focus:z-1',
                'disabled:ring-greyLight disabled:focus:ring-grey',
                'h-auto',
                transparent && 'shadow-none ring-transparent focus:ring-transparent disabled:ring-transparent',
                error && 'ring-red border-red focus:ring-red disabled:ring-red read-only:ring-red',
              ),
              dropdownStyleProps: {
                className: '!bg-white !shadow-md !text-inherit border rounded-lg p-0',
                listItemClassName:
                  '!bg-white !text-inherit border-b last:border-b-0 hover:!bg-greyLightest aria-selected:!bg-greyLightest',
                listItemCountryNameClassName: '!text-inherit',
                listItemDialCodeClassName: '!text-sm !text-greyDark',
              },
            },
            inputClassName: cn(
              'grow block outline-0 !rounded-r-md !border-0 shadow-sm placeholder:text-grey !px-3 !py-2 !text-inherit !bg-white',
              'ring-1 ring-inset ring-grey',
              'focus:ring-greyDark focus:z-1',
              'disabled:ring-greyLight disabled:focus:ring-grey',
              'read-only:ring-greyLight read-only:focus:ring-grey',
              '!h-auto',
              transparent &&
                'shadow-none ring-transparent focus:ring-transparent disabled:ring-transparent read-only:ring-transparent',
              error && 'ring-red focus:ring-red disabled:ring-red read-only:ring-red',
              inputClassName,
            ),
          }}
          ref={ref}
          required={required}
          type={type}
        />
      )
    }

    return (
      <div className={cn('Input', className)}>
        {label || labelComment || labelInfo ? (
          <label className="block font-bold mb-1" htmlFor={htmlId}>
            {label}
            {labelComment ? <small className="font-normal pl-1 leading-4">{labelComment}</small> : null}
            {required ? <sup className="text-red pl-1 leading-4 align-top">*</sup> : ''}
            {labelInfo ? (
              <Tooltip content={labelInfo}>
                <InformationCircleIcon className="size-4 ml-1 leading-4 text-blue align-top cursor-pointer" />
              </Tooltip>
            ) : null}
          </label>
        ) : null}
        <Tooltip
          className={cn('border-redLight stroke-redLight', tooltipClassName)}
          content={error}
          disabled={!error}
          initialOpen
        >
          {input}
        </Tooltip>
      </div>
    )
  },
)

Input.displayName = 'Input'
